import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress
} from '@mui/material';
import { annotationsApi } from '../app/annotations.js';

/**
 * ViewSelectorModal - A reusable modal component for selecting a view
 * 
 * @param {boolean} open - Controls the modal visibility
 * @param {function} onClose - Callback when modal is closed
 * @param {function} onSelectView - Callback when a view is selected (receives view object with id, project, inspection)
 * @param {string} defaultInspectionId - Pre-select the first view of this inspection (original view)
 * @param {string} defaultViewId - Fallback pre-selection if no inspection match
 */
function ViewSelectorModal({ open, onClose, onSelectView, defaultInspectionId, defaultViewId }) {
  const [accessibleViews, setAccessibleViews] = useState([]);
  const [selectedViewId, setSelectedViewId] = useState('');
  const [loading, setLoading] = useState(false);

  const pickDefaultViewId = (views) => {
    if (!views?.length) return '';
    if (defaultInspectionId) {
      const inspectionViews = views.filter((v) => v.inspection?.id === defaultInspectionId);
      if (inspectionViews.length > 0) {
        return inspectionViews[0].id;
      }
    }
    if (defaultViewId && views.some((v) => v.id === defaultViewId)) {
      return defaultViewId;
    }
    return views[0].id;
  };

  // Load accessible views when modal opens
  useEffect(() => {
    if (open) {
      setLoading(true);
      setSelectedViewId('');
      annotationsApi.availableViews((views) => {
        const list = views || [];
        setAccessibleViews(list);
        setSelectedViewId(pickDefaultViewId(list));
        setLoading(false);
      });
    }
  }, [open, defaultInspectionId, defaultViewId]);

  const handleUseView = () => {
    if (!selectedViewId) {
      return;
    }

    const selectedView = accessibleViews.find(v => v.id === selectedViewId);
    if (selectedView && onSelectView) {
      onSelectView(selectedView);
    }
    handleClose();
  };

  const handleClose = () => {
    setSelectedViewId('');
    if (onClose) {
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Select a view as template</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : accessibleViews.length === 0 ? (
            <Typography color="text.secondary">
              No views available
            </Typography>
          ) : (
            <FormControl fullWidth>
              <InputLabel>Select a view</InputLabel>
              <Select
                value={selectedViewId}
                onChange={(e) => setSelectedViewId(e.target.value)}
                label="Select a view"
              >
                {accessibleViews.map((view) => (
                  <MenuItem key={view.id} value={view.id}>
                    {view.project?.name || "Unknown Project"} - {view.inspection?.name || "Unknown Inspection"}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button 
          onClick={handleUseView} 
          variant="contained"
          disabled={!selectedViewId || loading}
        >
          Use view
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ViewSelectorModal;

